import { useQuery } from '@tanstack/react-query';
import { fetchPpaViewMetadata } from 'hooks/ppa/usePpaViews';
import type { SimulationFilterEditedAction } from 'types/ppaSimulationMeta';
import type { PPAViewDataSource, PPAViewLensItem } from 'types/ppaViews';

function mapViewLensesToEditedActionTypes(
    lenses: PPAViewLensItem[],
): SimulationFilterEditedAction[] {
    return lenses.map((lens) => ({
        id: lens.lens_type?.trim() || 'lens',
        input_value: lens.lens_name.trim().toLowerCase().replace(/\s+/g, '_'),
        label: lens.lens_name,
    }));
}

type UseSimulatorFilterEditedActionsArgs = {
    viewId?: number | null;
    scenarioId?: number | string | null;
    dataSource?: PPAViewDataSource;
    enabled?: boolean;
    fallbackActionTypes?: SimulationFilterEditedAction[];
};

function resolveEditedActionsScenarioId(scenarioId?: number | string | null): number | null {
    if (scenarioId == null || scenarioId === '') {
        return null;
    }

    return typeof scenarioId === 'number' ? scenarioId : Number(scenarioId);
}

export function useSimulatorFilterEditedActions({
    viewId,
    scenarioId,
    dataSource = 'view',
    enabled = false,
    fallbackActionTypes = [],
}: UseSimulatorFilterEditedActionsArgs) {
    const resolvedScenarioId = resolveEditedActionsScenarioId(scenarioId);
    const query = useQuery({
        queryKey: ['simulator-filter-edited-actions', viewId, resolvedScenarioId, dataSource],
        enabled: enabled && viewId != null,
        queryFn: async () => {
            const metadata = await fetchPpaViewMetadata(viewId!, { scenarioId: resolvedScenarioId, dataSource });
            return mapViewLensesToEditedActionTypes(metadata.lenses);
        },
    });

    const actionTypes =
        query.data && query.data.length > 0 ? query.data : fallbackActionTypes;

    return {
        actionTypes,
        isLoading: query.isLoading,
    };
}
